/* ═══════════════════════════════════════════════════════════════════════════
   REGISTAR PROPISA — jedino mesto na kome stoji „koje pravilo pripada kom propisu".

   Zašto postoji: do sada su AETR pravila i domaći propisi bili pomešani u jednoj
   listi prekršaja, pa se nije moglo odgovoriti na pitanje „pokaži mi samo ono što
   kontroliše inspekcija po AETR-u". Uz to, kad se pojavi nova revizija (AETR 2020
   ili novi domaći zakon), menjati motor nije rešenje.

   Kako se dodaje nov propis:
     1. nova stavka u PROPISI sa svojim `limiti` i spiskom `pravila`;
     2. ako nosi novo pravilo — funkcija u aetrMotor.js i njen ključ u `pravila`;
     3. ako menja iznose — nova stavka u TARIFIKATORI.
   Motor (aetrMotor.js) se NE menja: limite i spisak pravila dobija odavde.

   Napomena o dve nezavisne stvari:
     PROPIS       — šta je prekršaj i koji su limiti (AETR, domaći zakon).
     TARIFIKATOR  — koliko se plaća. Za AETR prekršaje u Srbiji kazne propisuje
                    DOMAĆI zakon (čl. 54–61), pa Tachospeed izveštaj i piše
                    „AETR 2010, Srpski tarifikator". Zato su razdvojeni.
   ═══════════════════════════════════════════════════════════════════════════ */

const MIN = 60 * 1000;
const H = 60 * MIN;

/* ── OSNOVNI LIMITI — mehanika motora, ne pravilo jednog propisa ───────────

   Ovo su vrednosti kojima motor REŽE podatke na periode rad/odmor, pre nego što
   bilo koje pravilo progovori. Moraju važiti i kad se kontroliše samo jedan
   propis, inače motor ostane bez praga i periodi se ne sklope.

   (Naučeno na testu: kad je `zatvaraPeriod` stajao samo u AETR limitima, izbor
   „samo domaći propisi" je davao besmislene periode, jer je poređenje sa
   `undefined` uvek netačno.) */
export const LIMITI_OSNOVNI = {
    /* Prag kojim se ZATVARA dnevni period upravljanja.

       SADA JE 9h — isto kao do sada, da uvođenje registra ne promeni ni jedan
       rezultat. Tachospeed koristi **7h** (EC 3759/2011), pa se kod nas odmor od
       7–9h stapa u rad i dva dana postanu jedan period sa dvostrukom vožnjom.
       Prelaz na 7h menja SVE izvedene veličine (vožnja, radno vreme, broj
       perioda), zato ide kao zaseban korak uz regresioni test.
       Vidi TASK/TAHOGRAF/ANALIZA.md, nalaz 6a.

       Kad dođe red: promeni samo ovu vrednost na 7 * H. */
    zatvaraPeriod: 9 * H,
};

/* ── PROPISI ──────────────────────────────────────────────────────────────── */

export const PROPISI = {

    AETR_2010: {
        kljuc: 'AETR_2010',
        naziv: 'AETR 2010',
        /* Prikaz ide kroz i18n, da registar ostane jedini izvor i za ime propisa. */
        i18nKljuc: 'mk.propisAETR2010',
        punNaziv: 'Sporazum AETR, revizija iz 2010 — istovetan sa EU 561/2006',
        /* Revizija iz 2010 je AETR izjednačila sa EU 561/2006, pa su svi limiti
           ispod identični u oba teksta. Zato se jedan motor koristi za oba. */
        osnov: 'AETR čl. 6 (vreme upravljanja), čl. 7 (pauze), čl. 8 (odmori)',
        vaziOd: '2010-09-20',
        pravila: ['dayRest', 'sumDriving', 'twoWeek', 'less45', 'weekRest'],
        limiti: {
            dnevnaVoznja:              9 * H,
            dnevnaVoznjaProduzena:    10 * H,
            produzenihNedeljno:        2,
            nedeljnaVoznja:           56 * H,
            dvonedeljnaVoznja:        90 * H,
            neprekidnaVoznja:        4.5 * H,
            pauza:                    45 * MIN,
            pauzaPrvi:                15 * MIN,
            pauzaDrugi:               30 * MIN,
            dnevniOdmor:              11 * H,
            dnevniOdmorSkraceni:       9 * H,
            skracenihIzmedjuNedeljnih: 3,
            /* Radni period = od kraja prethodnog odmora do početka sledećeg. Dnevni
               odmor mora biti završen u okviru 24h, pa je najduži dozvoljen radni
               period 24h − 9h = 15h. */
            najduziRadniPeriod:       15 * H,
            nedeljniOdmor:            45 * H,
            nedeljniOdmorSkraceni:    24 * H,
            nedeljniNajkasnije:   6 * 24 * H,
            /* Redovan dnevni odmor sme biti podeljen na 3h + 9h (EU 561/2006
               čl. 4(g)) — tada NIJE skraćen i ne troši jedno od tri skraćenja. */
            podeljenPrviDeo:           3 * H,
            podeljenDrugiDeo:          9 * H,
        },
    },

    RS_2015: {
        kljuc: 'RS_2015',
        naziv: 'Propisi Republike Srbije',
        i18nKljuc: 'mk.propisSrbija',
        punNaziv: 'Zakon o radnom vremenu posade vozila u drumskom prevozu i tahografima '
                + '(„Sl. glasnik RS" 96/2015, 95/2018)',
        osnov: 'čl. 4 (dnevno radno vreme), čl. 40–41 (noćni rad)',
        vaziOd: '2015-11-28',
        /* Važi SAMO za firme registrovane u Srbiji. id_drzava se čita iz
           auth.firme_subscription; 1 = Srbija. Firma u Sloveniji ili Hrvatskoj ne
           sme da se sudi po srpskom zakonu — zato ova pravila uopšte ne ulaze u
           obračun kad država ne odgovara. Propis bez navedenih država (npr. AETR) važi svuda. */
        drzave: [1],
        pravila: ['workTime', 'nightDriving', 'sumWork', 'cardExpiry', 'unknown'],
        limiti: {
            /* čl. 4 st. 2 — najduže dnevno radno vreme u periodu od 24 časa */
            radnoVremeMax:            14 * H,
            /* čl. 4 st. 5 — ako se obavlja noćni rad */
            radnoVremeNocno:          10 * H,
            /* čl. 40 — noćno vreme; čl. 41 — noćni rad je rad u tom periodu */
            nocnoOdSat:                0,
            nocnoDoSat:                4,
            /* Pauza kraća od ovoga se ne priznaje — ulazi u radno vreme. */
            kratkaPauza:              15 * MIN,
            /* Noćna vožnja u prozoru 00:00–04:00 */
            nocnaVoznjaMax:            3 * H,
        },
    },
};

/** Redosled u kom se propisi prikazuju u izborniku. */
export const PROPISI_LISTA = [PROPISI.AETR_2010, PROPISI.RS_2015];

/** Podrazumevano se gleda SVE — da promena ne sakrije prekršaje koji su se do sada videli. */
export const SVI_PROPISI = PROPISI_LISTA.map(p => p.kljuc);

/* ── TARIFIKATORI ─────────────────────────────────────────────────────────── */

/* Kazne su u FIKSNOM iznosu i stepenovane po težini (4 nivoa). Nivo se u zakonu
   vidi kao BROJ ČLANA: čl. 54 (najteži) … čl. 57 (najblaži) za privredne subjekte,
   čl. 58–61 za vozača kao fizičko lice.

   Potvrđeno na 18 prekršaja iz 14 kontrolnih izveštaja/opomena (Tachospeed 2.991,
   „Srpski tarifikator (preduzeće)"): 12 od 12 iznosa koji se mogu uporediti
   poklapaju se do dinara. Vidi TASK/TAHOGRAF/ANALIZA.md, nalaz 4. */
export const TARIFIKATORI = {
    RS_2018: {
        kljuc: 'RS_2018',
        naziv: 'Srpski tarifikator',
        osnov: 'Zakon o radnom vremenu posade vozila, kaznene odredbe čl. 54–61',
        vaziOd: '2018-12-08',
        /* nivo → iznos po vrsti subjekta, i broj člana za privredni subjekt */
        nivoi: {
            1: { clan: 54, pravno: 200000, preduzetnik: 120000, odgovorno: 25000, vozac: 50000 },
            2: { clan: 55, pravno: 120000, preduzetnik:  80000, odgovorno: 15000, vozac: 40000 },
            3: { clan: 56, pravno:  60000, preduzetnik:  40000, odgovorno: 10000, vozac: 20000 },
            4: { clan: 57, pravno:  30000, preduzetnik:  20000, odgovorno:  5000, vozac: 10000 },
        },
    },
};

export const TARIFIKATOR_PODRAZUMEVANI = 'RS_2018';

/* ── KATEGORIJE ───────────────────────────────────────────────────────────── */

/* Kategorije onako kako ih vodi kontrolni izveštaj / opomena, u njihovom redosledu.
   `podtipovi` su naši `Podtip` iz motora — preslikavanje je VIŠE-NA-JEDAN, jer više
   naših podtipova opisuje isti prekršaj iz njihove tabele.

   `tacke` je broj člana za dati nivo (npr. nivo 2 → „čl. 55.18"). Popunjeno je SAMO
   tamo gde postoji dokaz u referentnim izveštajima — ostalo je `null` i NE SME se
   pogađati, jer broj člana ide u dokument koji potpisuje odgovorno lice. */
export const KATEGORIJE = [
    { redni: 1,  propis: 'AETR_2010', naziv: 'Prekršaj nedeljnog vremena upravljanja',
      podtipovi: ['Nedeljno'], tacke: {} },

    { redni: 2,  propis: 'AETR_2010', naziv: 'Prekršaj dvonedeljnog vremena upravljanja',
      podtipovi: ['Dvonedeljno'], tacke: { 4: '57.7' } },

    { redni: 3,  propis: 'AETR_2010', naziv: 'Prekoračenje dnevnog vremena upravljanja (9h)',
      podtipovi: ['Broj produženih'], tacke: {} },

    { redni: 4,  propis: 'AETR_2010', naziv: 'Prekoračenje dnevnog vremena upravljanja (10h)',
      podtipovi: ['Dnevno'], tacke: { 4: '57.5' } },

    { redni: 5,  propis: 'AETR_2010', naziv: 'Neprekidno upravljanje bez odgovarajućih pauza',
      podtipovi: ['4h30 bez pauze'], tacke: { 3: '56.11', 4: '57.8' } },

    { redni: 6,  propis: 'AETR_2010', naziv: 'Neprekidno upravljanje bez odgovarajućih pauza (3h)',
      podtipovi: [], samoAutobus: true, tacke: { 3: '56.11', 4: '57.8' } },

    { redni: 7,  propis: 'AETR_2010', naziv: 'Skraćenje nedeljnog odmora (24h)',
      podtipovi: ['Skraćen pod 24h'], tacke: {} },

    { redni: 8,  propis: 'AETR_2010', naziv: 'Skraćenje nedeljnog odmora (45h)',
      podtipovi: ['Dva skraćena za redom'], tacke: {} },

    { redni: 9,  propis: 'AETR_2010', naziv: 'Nedovoljna nadoknada skraćenog nedeljnog odmora',
      podtipovi: ['Nenadoknađen'], tacke: { 2: '55.28' } },

    { redni: 10, propis: 'AETR_2010', naziv: 'Skraćenje nedeljnog odmora (69h)',
      podtipovi: ['Zbir dva nedeljna pod 69h'], tacke: {} },

    { redni: 11, propis: 'AETR_2010', naziv: 'Nedovoljan period između nedeljnih odmora',
      podtipovi: ['Kasno započet', 'Nema odmora', 'Nema odmora u periodu'], tacke: { 4: '57.18' } },

    { redni: 12, propis: 'AETR_2010', naziv: 'Nedovoljan period između nedeljnih odmora (autobus)',
      podtipovi: [], samoAutobus: true, tacke: {} },

    /* Naša dva podtipa slivaju se u JEDNU njihovu kategoriju. „Nije u 24h" je isti
       događaj kao „Nedovoljan" — Tachospeed ga vodi kao nedostatak odmora u 24h
       kontrolnom prozoru. Bez ovog slivanja isti prekršaj bi se u dokumentu
       pojavio dvaput. Vidi ANALIZA.md, odeljak o preslikavanju. */
    { redni: 13, propis: 'AETR_2010', naziv: 'Skraćenje dnevnog odmora (9h)',
      podtipovi: ['Nedovoljan', 'Nije u 24h'],
      tacke: { 2: '55.18', 3: '56.17', 4: '57.13' } },

    { redni: 14, propis: 'AETR_2010', naziv: 'Skraćenje dnevnog odmora (9h, posada)',
      podtipovi: [], samoPosada: true, tacke: {} },

    { redni: 15, propis: 'AETR_2010', naziv: 'Skraćenje dnevnog odmora (11h)',
      podtipovi: ['Broj skraćenih'], tacke: { 3: '56.16' } },

    /* ── Domaći propisi — Tachospeed ih ne obrađuje, ali su prekršaji po zakonu
          i idu u zaseban pregled. ───────────────────────────────────────────── */
    { redni: 101, propis: 'RS_2015', naziv: 'Prekoračeno dnevno radno vreme (14h)',
      podtipovi: ['Dnevno (14h)'], tacke: {} },
    { redni: 102, propis: 'RS_2015', naziv: 'Prekoračeno dnevno radno vreme uz noćni rad (10h)',
      podtipovi: ['Noćni rad (10h)'], tacke: {} },
    { redni: 103, propis: 'RS_2015', naziv: 'Prekoračena noćna vožnja',
      podtipovi: ['00:00–04:00'], tacke: {} },
    { redni: 104, propis: 'RS_2015', naziv: 'Ukupan rad bez propisane pauze',
      podtipovi: ['Ukupan rad'], tacke: {} },
    { redni: 105, propis: 'RS_2015', naziv: 'Vožnja sa isteklom karticom',
      podtipovi: ['Istek kartice'], tacke: {} },
    { redni: 106, propis: 'RS_2015', naziv: 'Loše upravljanje tahografom',
      podtipovi: ['Nepoznata aktivnost'], tacke: {} },
];

/* ── pomoćne funkcije ─────────────────────────────────────────────────────── */

/**
 * Da li propis važi za datu državu firme (`auth.firme_subscription.id_drzava`).
 *
 * Propis bez `drzave` važi svuda (AETR). Propis sa spiskom važi samo tamo.
 *
 * Kad država NIJE poznata (NULL u bazi, kao kod firmi 7/8/9) propis vezan za
 * državu se NE primenjuje. Namerno strože: pogrešno naplaćena kazna po tuđem
 * zakonu je gora greška od propuštene, a leči se upisom `id_drzava`.
 */
export function propisVaziZaDrzavu(propis, idDrzava) {
    if (!propis) return false;
    if (!Array.isArray(propis.drzave)) return true;      // bez ograničenja → svuda
    const d = Number(idDrzava);
    return Number.isFinite(d) && propis.drzave.includes(d);
}

/** Propisi koji se SMEJU primeniti na firmu u datoj državi, u redosledu prikaza. */
export function propisiZaDrzavu(idDrzava) {
    return PROPISI_LISTA.filter(p => propisVaziZaDrzavu(p, idDrzava));
}

/** Ključevi tih propisa — za podrazumevani izbor „svi (koji važe)". */
export function kljuceviZaDrzavu(idDrzava) {
    return propisiZaDrzavu(idDrzava).map(p => p.kljuc);
}

/**
 * Propisi za date ključeve; prazno/nepoznato → svi.
 *
 * `idDrzava` je sigurnosna kapija: kad se preda, propis koji ne važi u toj državi
 * se izbacuje čak i ako ga je pozivalac izričito tražio. Tako ni pogrešan poziv
 * ne može da primeni srpski zakon na slovenačku firmu.
 */
export function propisiZa(kljucevi, idDrzava) {
    const lista = Array.isArray(kljucevi) && kljucevi.length ? kljucevi : SVI_PROPISI;
    const izabrani = lista.map(k => PROPISI[k]).filter(Boolean);
    return idDrzava === undefined || idDrzava === null
        ? izabrani
        : izabrani.filter(p => propisVaziZaDrzavu(p, idDrzava));
}

/**
 * Spojeni limiti izabranih propisa.
 * Ako dva propisa definišu isti limit, pobeđuje onaj NAVEDEN KASNIJE u `kljucevi` —
 * tako se nova revizija dodaje na kraj i pregazi staru vrednost, bez izmene motora.
 */
export function limitiZa(kljucevi, idDrzava) {
    return propisiZa(kljucevi, idDrzava).reduce((a, p) => ({ ...a, ...p.limiti }), { ...LIMITI_OSNOVNI });
}

/** Ključevi pravila koja pripadaju izabranim propisima. */
export function pravilaZa(kljucevi, idDrzava) {
    return propisiZa(kljucevi, idDrzava).reduce((a, p) => a.concat(p.pravila), []);
}

/** Kom propisu pripada dato pravilo (ključ grupe iz motora). */
export function propisPravila(kljucPravila) {
    const p = PROPISI_LISTA.find(x => x.pravila.includes(kljucPravila));
    return p ? p.kljuc : null;
}

/** Kategorija (red u kontrolnom izveštaju) za dati Podtip iz motora. */
export function kategorijaZa(podtip) {
    if (!podtip) return null;
    return KATEGORIJE.find(k => k.podtipovi.includes(podtip)) || null;
}

/**
 * Broj člana za prekršaj datog podtipa i nivoa — npr. „čl. 55.18".
 * Vraća null kad tačka nije poznata; pozivalac tada ne sme ništa da izmisli.
 */
export function clanZa(podtip, nivo, tarifikator = TARIFIKATOR_PODRAZUMEVANI) {
    const kat = kategorijaZa(podtip);
    const t = TARIFIKATORI[tarifikator];
    if (!kat || !t || !t.nivoi[nivo]) return null;
    const tacka = kat.tacke[nivo];
    return tacka ? `čl. ${tacka}` : `čl. ${t.nivoi[nivo].clan}`;
}

/** Iznosi za dati nivo iz izabranog tarifikatora. */
export function iznosiZa(nivo, tarifikator = TARIFIKATOR_PODRAZUMEVANI) {
    const t = TARIFIKATORI[tarifikator];
    return (t && t.nivoi[nivo]) || (t && t.nivoi[4]) || null;
}

/** Naziv propisa za prikaz („AETR 2010", „Domaći propisi"). */
export function nazivPropisa(kljuc) {
    return PROPISI[kljuc] ? PROPISI[kljuc].naziv : kljuc || '';
}

/** Zaglavlje izveštaja — isto kao kod njih: „Kontrola prema AETR 2010, Srpski tarifikator". */
export function zaglavljeKontrole(kljucevi, tarifikator = TARIFIKATOR_PODRAZUMEVANI, subjekt = 'pravno', idDrzava) {
    const propisi = propisiZa(kljucevi, idDrzava).map(p => p.naziv).join(' + ');
    const t = TARIFIKATORI[tarifikator];
    const subjekti = { pravno: 'pravno lice', preduzetnik: 'preduzetnik',
                       odgovorno: 'odgovorno lice', vozac: 'vozač' };
    return `Kontrola prema ${propisi}, ${t ? t.naziv : ''} (${subjekti[subjekt] || subjekt})`;
}

export default PROPISI;
